import React from 'react';

/**
 * Oola 7 F category icons — Lucide-style line SVGs for Dashboard V2.
 * Subtle stroke icons; no fills / cartoon glyphs.
 */

const SVG_PROPS = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
};

export const F_CATEGORIES = [
    'Fitness',
    'Finance',
    'Family',
    'Field',
    'Faith',
    'Friends',
    'Fun',
];

/** Canonical Oola F colors (source of truth for brand). */
export const F_CATEGORY_COLORS = {
    Fitness: '#15C184',
    Finance: '#9AC864',
    Family: '#FABE53',
    Field: '#F16636',
    Faith: '#EB0F47',
    Friends: '#700460',
    Fun: '#022B7A',
};

/**
 * Soft but vivid accents for dark UI (journey markers / legend).
 * Lifted for contrast on the mountain; closer to brand than washed pastels.
 */
export const F_CATEGORY_PASTEL_COLORS = {
    Fitness: '#2ED99A',
    Finance: '#A8DC52',
    Family: '#FFC857',
    Field: '#FF7540',
    Faith: '#FF3D6E',
    Friends: '#C24AB0',
    Fun: '#4A8AE8',
};

function mixHexTowardWhite(hex, amount = 0.35) {
    if (!hex || typeof hex !== 'string') return null;
    const raw = hex.replace('#', '').trim();
    const full =
        raw.length === 3
            ? raw
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : raw;
    if (full.length !== 6) return null;
    const n = parseInt(full, 16);
    if (Number.isNaN(n)) return null;
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    const t = Math.min(1, Math.max(0, amount));
    const nr = Math.round(r + (255 - r) * t);
    const ng = Math.round(g + (255 - g) * t);
    const nb = Math.round(b + (255 - b) * t);
    return `#${[nr, ng, nb]
        .map((v) => v.toString(16).padStart(2, '0'))
        .join('')}`;
}

/**
 * Accent for journey markers: pastelized F color (Family/Fun emphasized).
 */
export function getFCategoryPastelAccent(category, fallbackHex) {
    const key = resolveFCategory(category);
    if (key && F_CATEGORY_PASTEL_COLORS[key]) {
        return F_CATEGORY_PASTEL_COLORS[key];
    }
    const base =
        (key && F_CATEGORY_COLORS[key]) ||
        fallbackHex ||
        '#c9a227';
    return mixHexTowardWhite(base, 0.22) || base;
}

/** Lucide HeartPulse */
function HeartPulseIcon(props) {
    return (
        <svg {...SVG_PROPS} {...props}>
            <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
            <path d="M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5H20" />
        </svg>
    );
}

/** Lucide TrendingUp */
function TrendingUpIcon(props) {
    return (
        <svg {...SVG_PROPS} {...props}>
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
            <polyline points="16 7 22 7 22 13" />
        </svg>
    );
}

/** Lucide Home */
function HomeIcon(props) {
    return (
        <svg {...SVG_PROPS} {...props}>
            <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
            <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        </svg>
    );
}

/** Lucide Flag — fallback / Oola One accents */
function FlagIcon(props) {
    return (
        <svg {...SVG_PROPS} {...props}>
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
        </svg>
    );
}

/** Lucide Briefcase — Field */
function BriefcaseIcon(props) {
    return (
        <svg {...SVG_PROPS} {...props}>
            <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            <rect width="20" height="14" x="2" y="6" rx="2" />
        </svg>
    );
}

/** Lucide Sunrise — Faith */
function SunriseIcon(props) {
    return (
        <svg {...SVG_PROPS} {...props}>
            <path d="M12 2v2" />
            <path d="m4.93 10.93 1.41 1.41" />
            <path d="M2 18h2" />
            <path d="M20 18h2" />
            <path d="m19.07 10.93-1.41 1.41" />
            <path d="M22 22H2" />
            <path d="m8 6 4-4 4 4" />
            <path d="M16 18a4 4 0 0 0-8 0" />
        </svg>
    );
}

/** Lucide Users — Friends */
function UsersIcon(props) {
    return (
        <svg {...SVG_PROPS} {...props}>
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
    );
}

/** Lucide Smile — Fun */
function SmileIcon(props) {
    return (
        <svg {...SVG_PROPS} {...props}>
            <circle cx="12" cy="12" r="10" />
            <path d="M8 14s1.5 2 4 2 4-2 4-2" />
            <line x1="9" x2="9.01" y1="9" y2="9" />
            <line x1="15" x2="15.01" y1="9" y2="9" />
        </svg>
    );
}

/** Preferred mapping for each F */
export const F_CATEGORY_ICON_MAP = {
    Fitness: HeartPulseIcon,
    Finance: TrendingUpIcon,
    Family: HomeIcon,
    Field: BriefcaseIcon,
    Faith: SunriseIcon,
    Friends: UsersIcon,
    Fun: SmileIcon,
};

/**
 * Resolve canonical F name from API areaName (case-insensitive, partial ok).
 */
export function resolveFCategory(areaName) {
    const raw = String(areaName || '')
        .trim()
        .toLowerCase();
    if (!raw) return null;
    return (
        F_CATEGORIES.find((name) => name.toLowerCase() === raw) ||
        F_CATEGORIES.find((name) => raw.includes(name.toLowerCase())) ||
        null
    );
}

/**
 * Line icon for a 7 F category. Falls back to Flag when unknown.
 */
export default function FCategoryIcon({
    category,
    size = 16,
    className = '',
    title,
}) {
    const key = resolveFCategory(category);
    const Icon = (key && F_CATEGORY_ICON_MAP[key]) || FlagIcon;
    return (
        <Icon
            width={size}
            height={size}
            className={className}
            title={title || key || category || undefined}
        />
    );
}
